import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Radio, UserPlus, ArrowRight, Eye } from 'lucide-react';
import { api } from '../lib/api';
import { RVP_LIST } from '../data/rendezvousPedagogiques';
import Logo from '../components/Logo';

export default function RvpJoinPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState('');

  useEffect(() => {
    api.castGet(code)
      .then(setSession)
      .catch(() => setError('Session introuvable ou expirée. Vérifiez le code avec votre formateur.'));
  }, [code]);

  const rvp = session ? RVP_LIST.find((r) => r.slug === session.slug) : null;
  const followUrl = `/present/live/${code}?viewer=1`;

  const join = async () => {
    if (!prenom.trim()) { setJoinError('Indique au moins ton prénom — ou choisis « Suivre sans donner mon nom ».'); return; }
    setJoinError('');
    setJoining(true);
    try {
      await api.castJoin(code, { prenom: prenom.trim(), nom: nom.trim() || undefined });
      navigate(followUrl);
    } catch (err) {
      setJoining(false);
      setJoinError(err?.response?.data?.detail || 'Impossible de rejoindre. Réessaie.');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white flex flex-col items-center px-5 py-8">
      <Logo variant="horizontal" height={44} />
      {error ? (
        <div className="mt-16 max-w-sm text-center" data-testid="join-error">
          <p className="text-white/70 text-[14.5px] leading-relaxed">{error}</p>
        </div>
      ) : !session ? (
        <div className="mt-20 text-white/50 inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Connexion à la séance…</div>
      ) : (
        <div className="mt-8 w-full max-w-sm" data-testid="join-page">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-500/40 bg-red-500/10 text-red-300 text-[10.5px] uppercase tracking-[0.2em] font-bold font-mono-spec">
            <Radio size={11} /> Séance en direct · {code}
          </span>
          <h1 className="mt-3 font-serif-editorial text-[26px] leading-tight">{rvp?.title || 'Séance pédagogique'}</h1>
          <p className="mt-2 text-white/60 text-[13.5px] leading-relaxed">
            Indique ton prénom pour apparaître à l&apos;écran de bienvenue, suivre les diapositives et répondre aux questions
            depuis ton téléphone. <strong className="text-white/85">C&apos;est facultatif</strong> — tu peux aussi suivre anonymement.
          </p>
          <div className="mt-5 space-y-3">
            <input value={prenom} onChange={(e) => setPrenom(e.target.value)} data-testid="join-prenom" placeholder="Ton prénom"
              className="w-full h-12 px-4 rounded-xl bg-white/5 border border-white/15 text-white placeholder-white/35 text-[15px] focus:outline-none focus:border-red-500/60" />
            <input value={nom} onChange={(e) => setNom(e.target.value)} data-testid="join-nom" placeholder="Ton nom (facultatif)"
              className="w-full h-12 px-4 rounded-xl bg-white/5 border border-white/15 text-white placeholder-white/35 text-[15px] focus:outline-none focus:border-red-500/60" />
          </div>
          {joinError && <div className="mt-3 rounded-lg border border-red-500/40 bg-red-950/30 px-3 py-2 text-red-300 text-[12.5px]" data-testid="join-error-msg">{joinError}</div>}
          <button onClick={join} disabled={joining} data-testid="join-submit"
            className="mt-4 w-full h-13 py-3.5 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-[15px] font-bold inline-flex items-center justify-center gap-2 transition">
            {joining ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />} Rejoindre la séance
          </button>
          <button onClick={() => navigate(followUrl)} data-testid="join-anonymous"
            className="mt-3 w-full h-12 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white/80 text-[13.5px] font-semibold inline-flex items-center justify-center gap-2 transition">
            <Eye size={14} /> Suivre sans donner mon nom <ArrowRight size={13} />
          </button>
          <p className="mt-5 text-center text-white/35 text-[11px] leading-relaxed">
            Aucun compte requis · ton prénom sert uniquement à l&apos;affichage pendant la séance.
            À la fin, tu retrouveras le rappel complet du cours sur ton téléphone.
          </p>
        </div>
      )}
    </div>
  );
}
